"use client";

import { useState, useEffect, useRef } from "react";
import { X, CheckCircle, AlertCircle, Loader2 } from "lucide-react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { submitEnquiry } from "@/actions/enquiry";

interface QuickEnquiryModalProps {
    isOpen: boolean;
    onClose: () => void;
}

const SERVICE_OPTIONS = [
    "IT Support",
    "WiFi/LAN Setup",
    "CCTV Installation",
    "PABX Systems",
    "Network Cabling",
    "Office 365 / Google Workspace Setup",
    "Server Management",
    "Cybersecurity",
    "Cloud Services",
    "Web Management & Hosting",
    "IT AMC (Annual Maintenance)",
    "Other"
];

const URGENCY_LEVELS = [
    "Standard (1-2 business days)",
    "Urgent (Same day/Next day)",
    "Immediate (Within 4 hours)"
];

const BUDGET_RANGES = [
    "Under LKR 150,000",
    "LKR 150,000 - 500,000",
    "LKR 500,000 - 1,500,000",
    "Over LKR 1,500,000",
    "Prefer to discuss"
];

const CONTACT_TIMES = [
    "Morning (9AM-12PM)",
    "Afternoon (12PM-5PM)",
    "Evening (5PM-8PM)",
    "Anytime"
];

export function QuickEnquiryModal({ isOpen, onClose }: QuickEnquiryModalProps) {
    const [step, setStep] = useState(1);
    const [formData, setFormData] = useState({
        selectedServices: [] as string[],
        otherService: "",
        serviceDescription: "",
        fullName: "",
        companyName: "",
        phoneNumber: "",
        email: "",
        preferredTime: "Anytime",
        urgency: "Standard (1-2 business days)",
        location: "",
        budget: "Prefer to discuss"
    });
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);

    // Animation State
    const [shouldRender, setShouldRender] = useState(isOpen);
    const containerRef = useRef<HTMLDivElement>(null);
    const backdropRef = useRef<HTMLDivElement>(null);
    const modalRef = useRef<HTMLDivElement>(null);

    // Sync render state with open state immediately for entry
    if (isOpen && !shouldRender) {
        setShouldRender(true);
    }

    // Effect for body scroll lock
    useEffect(() => {
        if (isOpen) {
            setStep(1);
            setIsSuccess(false);
            setErrors({});
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
    }, [isOpen]);

    // Separate effect to unlock body when strictly fully closed
    useEffect(() => {
        if (!shouldRender) {
            document.body.style.overflow = 'unset';
        }
    }, [shouldRender]);

    useGSAP(() => {
        if (isOpen && shouldRender) {
            // ENTRY ANIMATION
            // Set initial states
            gsap.set(backdropRef.current, { opacity: 0 });
            gsap.set(modalRef.current, { opacity: 0, scale: 0.95, y: 20 });

            // Animate In
            const tl = gsap.timeline();
            tl.to(backdropRef.current, { opacity: 1, duration: 0.3, ease: "power2.out" })
                .to(modalRef.current, { opacity: 1, scale: 1, y: 0, duration: 0.4, ease: "back.out(1.2)" }, "-=0.2");
        } else if (!isOpen && shouldRender) {
            // EXIT ANIMATION
            const tl = gsap.timeline({
                onComplete: () => setShouldRender(false)
            });
            tl.to(modalRef.current, { opacity: 0, scale: 0.95, y: 20, duration: 0.3, ease: "power2.in" })
                .to(backdropRef.current, { opacity: 0, duration: 0.3, ease: "power2.in" }, "-=0.2");
        }
    }, { scope: containerRef, dependencies: [isOpen, shouldRender] });


    const handleServiceToggle = (service: string) => {
        setFormData(prev => {
            const current = prev.selectedServices;
            if (current.includes(service)) {
                return { ...prev, selectedServices: current.filter(s => s !== service) };
            } else {
                return { ...prev, selectedServices: [...current, service] };
            }
        });
        if (errors.selectedServices) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors.selectedServices;
                return newErrors;
            });
        }
    };

    const validateStep1 = () => {
        if (formData.selectedServices.length === 0) {
            setErrors({ selectedServices: "Please select at least one service." });
            return false;
        }
        if (formData.selectedServices.includes("Other") && !formData.otherService.trim()) {
            setErrors({ otherService: "Please specify the other service." });
            return false;
        }
        return true;
    };

    const validateStep2 = () => {
        const newErrors: Record<string, string> = {};

        if (formData.serviceDescription.length < 10) {
            newErrors.serviceDescription = "Description must be at least 10 characters.";
        }
        if (!formData.fullName.trim()) {
            newErrors.fullName = "Full Name is required.";
        }
        if (!/^\+94\d{9}$/.test(formData.phoneNumber.replace(/\s/g, ''))) {
            if (!formData.phoneNumber.trim()) {
                newErrors.phoneNumber = "Phone Number is required.";
            }
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = "Please enter a valid email address.";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!validateStep2()) return;
        setIsSubmitting(true);

        try {
            const result = await submitEnquiry(formData);
            if (result.success) {
                console.log("Form Submitted Successfully");
                setIsSuccess(true);
            } else {
                console.error("Submission failed:", result.message);
                alert("Failed to submit enquiry. Please try again.");
            }
        } catch (error) {
            console.error("Submission error:", error);
            alert("An error occurred. Please try again later.");
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!shouldRender) return null;

    return (
        <div ref={containerRef} className="fixed inset-0 z-[100] flex items-center justify-center">
            {/* Backdrop */}
            <div
                ref={backdropRef}
                onClick={onClose}
                className="fixed inset-0 bg-black/60 backdrop-blur-sm"
            />

            {/* Modal */}
            <div
                ref={modalRef}
                className="relative z-[101] w-full max-w-2xl p-4 flex items-center justify-center pointer-events-none"
            >
                <div className="bg-white pointer-events-auto w-full rounded-2xl shadow-2xl flex flex-col max-h-[90vh] overflow-hidden">
                    {/* Header */}
                    <div className="flex justify-between items-center p-6 border-b border-gray-100 bg-[#001746] text-white">
                        <div>
                            <h2 className="text-xl font-bold">Quick Enquiry</h2>
                            <p className="text-blue-200 text-sm mt-1">Tell us what you need and we&apos;ll get back to you.</p>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 hover:bg-white/10 rounded-full transition-colors"
                            aria-label="Close modal"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Content */}
                    <div
                        className="overflow-y-auto p-6 md:p-8 custom-scrollbar overscroll-contain"
                        data-lenis-prevent
                    >
                        {isSuccess ? (
                            <div className="text-center py-12">
                                <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <CheckCircle className="w-8 h-8" />
                                </div>
                                <h3 className="text-2xl font-bold text-gray-900 mb-2">Request Received!</h3>
                                <p className="text-gray-500 max-w-md mx-auto">
                                    Thank you for submitting your enquiry. Our team will analyze your requirements and contact you shortly.
                                </p>
                                <button
                                    onClick={onClose}
                                    className="mt-8 px-6 py-2 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 transition-colors"
                                >
                                    Close
                                </button>
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit}>
                                {/* Step 1: Services */}
                                <div className={`space-y-6 ${step === 1 ? 'block' : 'hidden'}`}>
                                    <div>
                                        <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-4 border-l-4 border-blue-600 pl-3">
                                            1. Select Services (Required)
                                        </h3>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                            {SERVICE_OPTIONS.map((service) => (
                                                <label
                                                    key={service}
                                                    className={`
                                                        flex items-center p-3 rounded-lg border cursor-pointer transition-all
                                                        ${formData.selectedServices.includes(service)
                                                            ? "border-blue-500 bg-blue-50 text-blue-700 font-medium shadow-sm"
                                                            : "border-gray-200 hover:border-blue-300 hover:bg-gray-50 text-gray-700"}
                                                    `}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mr-3"
                                                        checked={formData.selectedServices.includes(service)}
                                                        onChange={() => handleServiceToggle(service)}
                                                    />
                                                    <span className="text-sm">{service}</span>
                                                </label>
                                            ))}
                                        </div>
                                        {formData.selectedServices.includes("Other") && (
                                            <div className="mt-3">
                                                <input
                                                    type="text"
                                                    placeholder="Please specify..."
                                                    className="w-full p-2 border border-blue-200 rounded-md focus:ring-2 focus:ring-blue-500 outline-none text-sm"
                                                    value={formData.otherService}
                                                    onChange={(e) => setFormData({ ...formData, otherService: e.target.value })}
                                                />
                                                {errors.otherService && <p className="text-red-500 text-xs mt-1">{errors.otherService}</p>}
                                            </div>
                                        )}
                                        {errors.selectedServices && (
                                            <div className="flex items-center gap-2 text-red-500 text-sm mt-3">
                                                <AlertCircle className="w-4 h-4" />
                                                {errors.selectedServices}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Step 2: Details & Contact */}
                                <div className={`space-y-8 ${step === 2 ? 'block' : 'hidden'}`}>

                                    {/* Service Details */}
                                    <div>
                                        <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-4 border-l-4 border-blue-600 pl-3">
                                            2. Service Details
                                        </h3>
                                        <div className="space-y-2">
                                            <label className="block text-sm font-medium text-gray-700">Description <span className="text-red-500">*</span></label>
                                            <textarea
                                                rows={5}
                                                className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition-all ${errors.serviceDescription ? 'border-red-300 focus:ring-red-200' : 'border-gray-200'}`}
                                                placeholder="Describe your requirements (number of users, current setup, timeline, location, budget range in LKR)"
                                                value={formData.serviceDescription}
                                                onChange={(e) => setFormData({ ...formData, serviceDescription: e.target.value })}
                                            />
                                            {errors.serviceDescription && <p className="text-red-500 text-xs">{errors.serviceDescription}</p>}
                                        </div>
                                    </div>

                                    {/* Contact Info */}
                                    <div>
                                        <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-4 border-l-4 border-blue-600 pl-3">
                                            3. Contact Information
                                        </h3>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Full Name <span className="text-red-500">*</span></label>
                                                <input
                                                    type="text"
                                                    className={`w-full p-2.5 border rounded-lg outline-none focus:ring-2 focus:ring-blue-500 ${errors.fullName ? 'border-red-300' : 'border-gray-200'}`}
                                                    value={formData.fullName}
                                                    onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                                                />
                                                {errors.fullName && <p className="text-red-500 text-xs mt-1">{errors.fullName}</p>}
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Company Name <span className="text-gray-400 font-normal">(Optional)</span></label>
                                                <input
                                                    type="text"
                                                    className="w-full p-2.5 border border-gray-200 rounded-lg outline-none focus:ring-2 focus:ring-blue-500"
                                                    value={formData.companyName}
                                                    onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number <span className="text-red-500">*</span></label>
                                                <input
                                                    type="text"
                                                    placeholder="+94 77 123 4567"
                                                    className={`w-full p-2.5 border rounded-lg outline-none focus:ring-2 focus:ring-blue-500 ${errors.phoneNumber ? 'border-red-300' : 'border-gray-200'}`}
                                                    value={formData.phoneNumber}
                                                    onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
                                                />
                                                {errors.phoneNumber && <p className="text-red-500 text-xs mt-1">{errors.phoneNumber}</p>}
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Email Address <span className="text-red-500">*</span></label>
                                                <input
                                                    type="email"
                                                    className={`w-full p-2.5 border rounded-lg outline-none focus:ring-2 focus:ring-blue-500 ${errors.email ? 'border-red-300' : 'border-gray-200'}`}
                                                    value={formData.email}
                                                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                                />
                                                {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Additional Context */}
                                    <div>
                                        <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-4 border-l-4 border-blue-600 pl-3">
                                            4. Additional Context
                                        </h3>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Preferred Contact Time</label>
                                                <select
                                                    className="w-full p-2.5 border border-gray-200 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                                                    value={formData.preferredTime}
                                                    onChange={(e) => setFormData({ ...formData, preferredTime: e.target.value })}
                                                >
                                                    {CONTACT_TIMES.map(t => <option key={t} value={t}>{t}</option>)}
                                                </select>
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Urgency Level</label>
                                                <select
                                                    className="w-full p-2.5 border border-gray-200 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                                                    value={formData.urgency}
                                                    onChange={(e) => setFormData({ ...formData, urgency: e.target.value })}
                                                >
                                                    {URGENCY_LEVELS.map(u => <option key={u} value={u}>{u}</option>)}
                                                </select>
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Service Location</label>
                                                <input
                                                    type="text"
                                                    placeholder="e.g., Colombo 7, Kandy"
                                                    className="w-full p-2.5 border border-gray-200 rounded-lg outline-none focus:ring-2 focus:ring-blue-500"
                                                    value={formData.location}
                                                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Budget Range (LKR)</label>
                                                <select
                                                    className="w-full p-2.5 border border-gray-200 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                                                    value={formData.budget}
                                                    onChange={(e) => setFormData({ ...formData, budget: e.target.value })}
                                                >
                                                    {BUDGET_RANGES.map(b => <option key={b} value={b}>{b}</option>)}
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </form>
                        )}
                    </div>

                    {/* Footer / Actions */}
                    {!isSuccess && (
                        <div className="p-6 border-t border-gray-100 bg-gray-50 flex justify-between items-center">
                            {step === 2 && (
                                <button
                                    type="button"
                                    onClick={() => setStep(1)}
                                    className="text-gray-600 font-medium hover:text-gray-900 px-4 py-2 hover:bg-gray-200 rounded-lg transition-colors"
                                    disabled={isSubmitting}
                                >
                                    Back
                                </button>
                            )}
                            <div className="ml-auto">
                                {step === 1 ? (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            if (validateStep1()) setStep(2);
                                        }}
                                        className="bg-blue-600 text-white px-8 py-2.5 rounded-full font-bold hover:bg-blue-700 transition-colors shadow-lg shadow-blue-200"
                                    >
                                        Next Step
                                    </button>
                                ) : (
                                    <button
                                        onClick={handleSubmit}
                                        disabled={isSubmitting}
                                        className="bg-green-600 text-white px-8 py-2.5 rounded-full font-bold hover:bg-green-700 transition-colors shadow-lg shadow-green-200 flex items-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
                                    >
                                        {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
                                        {isSubmitting ? "Submitting..." : "Submit Enquiry"}
                                    </button>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
